/**
 * Centralized filter counting utility for search pages.
 * Single source of truth for which filters are countable and how to count them.
 *
 * @module utils/filterCount
 */
import {
	newAmenities,
	defaultVehicleAmenitiesAndRequirements,
} from '../config/units';

/**
 * Extract countable amenity key from an amenity config object.
 * Supports both 'value' (newAmenities) and 'checkboxQueryParam' (legacy) properties.
 *
 * @param {Object} amenity - Amenity configuration object
 * @returns {string} The amenity key used in search params
 */
const getAmenityKey = (amenity) => amenity.checkboxQueryParam || amenity.value;

/**
 * Storage amenity keys derived from newAmenities (the UI source of truth).
 * Includes all 13 amenities displayed in SearchFilters.
 * @type {string[]}
 */
export const storageAmenityKeys = newAmenities.map(getAmenityKey);

/**
 * Legacy amenity keys that are no longer displayed in SearchFilters UI but
 * are still valid search params (see lib/search/index.js allowedSearchParams).
 * These may exist in bookmarked URLs or external links, so we count them
 * for backwards compatibility.
 * @type {string[]}
 */
export const legacyAmenityKeys = [
	'alarm',
	'cameras',
	'getOpenNow',
	'fencedAndLit',
];

/**
 * Vehicle filter keys derived from defaultVehicleAmenitiesAndRequirements.
 * @type {string[]}
 */
export const vehicleFilterKeys =
	defaultVehicleAmenitiesAndRequirements.map(getAmenityKey);

/**
 * All countable amenity keys - union of storage, vehicle, and legacy keys.
 * Used to ensure any active filter triggers Clear All, regardless of search type.
 * @type {string[]}
 */
export const allAmenityKeys = [
	...new Set([
		...storageAmenityKeys,
		...vehicleFilterKeys,
		...legacyAmenityKeys,
	]),
];

/**
 * Valid vehicle types that can appear in the vehicleTypes param.
 * These correspond to the Car/Boat/RV checkboxes in SearchFilters.
 * @type {string[]}
 */
export const validVehicleTypes = ['car', 'boat', 'rv'];

export const nearMeAmenityKeyMap = {
	'24-hour-storage': 'twentyFourHourAccess',
	'climate-controlled-storage': 'climateControlled',
	'drive-up-storage': 'driveUp',
	'storage-with-electricity': 'power',
	'wine-storage': 'onlyWine',
	'storage-with-free-truck-rental': 'freeTruckRental',
	'mini-storage': '0-25',
	'locker-storage': '0-5',
	'5x5-storage': '26-75',
	'5x10-storage': '26-75',
	'5x15-storage': '76-200',
	'10x10-storage': '76-200',
	'10x15-storage': '76-200',
	'10x20-storage': '201-999',
	'10x25-storage': '201-999',
	'10x30-storage': '201-999',
};

export const nearMeVehicleKeyMap = {
	'boat-storage': 'boat',
	'car-storage': 'car',
	'rv-storage': 'rv',
};

export const NEAR_ME_KEYWORD_TO_SIZE = {
	'5x5-storage': '5x5',
	'5x10-storage': '5x10',
	'5x15-storage': '5x15',
	'10x10-storage': '10x10',
	'10x15-storage': '10x15',
	'10x20-storage': '10x20',
	'10x25-storage': '10x25',
	'10x30-storage': '10x30',
	'mini-storage': '0-25',
	'locker-storage': '0-5',
};

export const vehicleUnitSizeFilterValues = [
	'15',
	'20',
	'25',
	'30',
	'35',
	'40',
	'50',
];

const aminityKeys = [
	'24-hour-storage',
	'climate-controlled-storage',
	'drive-up-storage',
	'storage-with-electricity',
	'wine-storage',
	'storage-with-free-truck-rental',
];

const storageUnitKeys = [
	'5x5-storage',
	'5x10-storage',
	'5x15-storage',
	'10x10-storage',
	'10x15-storage',
	'10x20-storage',
	'10x25-storage',
	'10x30-storage',
	'mini-storage',
	'locker-storage',
];

/**
 * Calculate total filter count from search params.
 *
 * Counted filters:
 * - ALL amenity/requirement checkboxes (storage, vehicle, legacy) - counted regardless of searchType
 * - Vehicle type selections (car, boat, rv) - each counts as 1, only for vehicle searchType
 * - Vehicle accommodation selection (outdoor-covered, outdoor-uncovered)
 * - Price filter (minPrice or maxPrice counts as 1)
 * - Specials/deals filter (hasSpecial)
 * - Review filter (averageReview)
 * - Unit size filters (sqft or onlyLocker count as ONE filter for storage)
 * - Vehicle length filter (minLength/maxLength counts as 1, only when no vehicleTypes selected)
 * - Near-me pre-selected amenity or vehicle (counts as 1 if not already in searchParams)
 *
 * @param {Object} searchParams - Current search parameters object
 * @param {string} [searchType='storage'] - Either 'storage' or 'vehicle'
 * @param {Object} [nearMeParams={}] - Near-me params from URL (storageAmenity, vehicle)
 * @returns {number} Total count of active filters
 */
export function calculateFilterCount(
	searchParams,
	searchType = 'storage',
	nearMeParams = {},
	location = {},
) {
	if (!searchParams) return 0;

	let count = 0;

	// 1. Count ALL amenity/requirement filters that are active
	// Using combined keys ensures Clear All activates regardless of how filters were set
	// (e.g., storage filters that persist after switching to vehicle search)
	if (location?.search) {
		allAmenityKeys.forEach((key) => {
			if (
				searchParams[key] === 'true' &&
				!(
					nearMeParams.storageAmenity &&
					nearMeAmenityKeyMap[nearMeParams.storageAmenity] === key
				)
			) {
				count++;
			}
		});
	}

	// 2. Vehicle type selections (car, boat, rv checkboxes)
	// Each selected type counts as 1 filter.
	if (location?.search && searchType === 'vehicle') {
		if (searchParams.vehicleTypes) {
			const selectedTypes = searchParams.vehicleTypes
				.split(',')
				.map((t) => t.trim());
			let validSelectedTypes;
			if (nearMeParams.vehicle) {
				validSelectedTypes = selectedTypes.filter(
					(t) =>
						validVehicleTypes.includes(t) &&
						t !== nearMeVehicleKeyMap[nearMeParams.vehicle],
				);
			} else {
				validSelectedTypes = selectedTypes.filter((t) =>
					validVehicleTypes.includes(t),
				);
			}
			count += validSelectedTypes.length;
		}
	}

	// 3. Vehicle accommodation (outdoor-covered, outdoor-uncovered)
	if (searchType === 'vehicle' && searchParams.vehicleAccommodation) {
		count++;
	}

	// 4. Price filter (slider sets both min and max, count as 1 filter)
	if (searchParams.minPrice || searchParams.maxPrice) {
		count++;
	}

	// 5. Special/deals filter
	if (searchParams.hasSpecial) {
		count++;
	}

	// 6. Review filter
	if (searchParams.averageReview) {
		count++;
	}

	// 7. Unit size filters
	// For storage: sqft or onlyLocker count as ONE filter
	if (
		location?.search &&
		!NEAR_ME_KEYWORD_TO_SIZE[nearMeParams.storageAmenity] &&
		(searchParams.sqft || searchParams.onlyLocker)
	) {
		count++;
	}

	// 8. Vehicle length filter
	// Only count if no vehicleTypes are selected - when vehicleTypes is set,
	// minLength/maxLength are auto-derived from the vehicle type selection
	// and counting them separately would be double-counting
	if (
		searchType === 'vehicle' &&
		searchParams.minLength &&
		vehicleUnitSizeFilterValues.includes(searchParams.minLength)
	) {
		count++;
	}

	// 9. Near-me pre-selected amenity — count only if not already reflected in searchParams
	if (nearMeParams.storageAmenity) {
		if (
			aminityKeys.includes(nearMeParams.storageAmenity) ||
			nearMeVehicleKeyMap[nearMeParams.storageAmenity]
		) {
			count++;
		}
		if (
			!searchParams.minLength &&
			storageUnitKeys.includes(nearMeParams.storageAmenity)
		) {
			count++;
		}
	}

	return count;
}
